import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { CustomerDefinedField as CustomerDefinedFields } from '@mynt/backoffice-api-spec/travel-card'
import useMaterialNotification from 'hooks/useMaterialNotification.tsx'
import isEqual from 'lodash/isEqual'
import { useId, useRef, useState } from 'react'
import { BorderContainer, BorderContainerHeader } from '../../../helpers/CreditOnCardHelpers'
import { useExternalCustomer, useGroupingConfig, useRegisterGroupingConfig } from '../api/useTravelCard'
import type { CustomerDefinedField, GroupingConfig, Naming, SpecialCase, TravelManagementCompany } from '../types'

const customerDefinedFields = Object.values(CustomerDefinedFields)

const inFieldOrder = (fields: CustomerDefinedField[]) =>
  [...fields].sort((a, b) => customerDefinedFields.indexOf(a) - customerDefinedFields.indexOf(b))

const emptySpecialCase: SpecialCase = {
  condition: { type: 'FIELD_STARTS_WITH', field: customerDefinedFields[0], prefix: '' },
  naming: { type: 'FIXED', name: '' },
}

const emptyNaming = (type: Naming['type']): Naming => (type === 'FIXED' ? { type, name: '' } : { type, fields: [] })

const isComplete = ({ condition, naming }: SpecialCase) =>
  !!condition.prefix.trim() && (naming.type === 'FIXED' ? !!naming.name.trim() : naming.fields.length > 0)

type GroupingConfigSectionProps = {
  customerId: string
}

export const GroupingConfigSection = ({ customerId }: GroupingConfigSectionProps) => {
  const { data: externalCustomer, isLoading, isError } = useExternalCustomer(customerId)
  const headingId = useId()

  return (
    <BorderContainer fullWidth gap="16px">
      <BorderContainerHeader style={{ marginBottom: 0 }}>
        <Typography id={headingId} variant="h3">
          Cost-center
        </Typography>
      </BorderContainerHeader>
      {isLoading && <CircularProgress size={24} />}
      {isError && <Alert severity="error">Could not load TMC account.</Alert>}
      {!isLoading && !isError && !externalCustomer && (
        <Alert severity="info">Register the customer at a TMC to configure the cost-center.</Alert>
      )}
      {externalCustomer && (
        <GroupingConfigContent customerId={customerId} tmc={externalCustomer.travelManagementCompany} labelledBy={headingId} />
      )}
    </BorderContainer>
  )
}

type GroupingConfigContentProps = {
  customerId: string
  tmc: TravelManagementCompany
  labelledBy: string
}

const GroupingConfigContent = ({ customerId, tmc, labelledBy }: GroupingConfigContentProps) => {
  const { data: groupingConfig, isLoading, isError } = useGroupingConfig(customerId, tmc)

  return (
    <>
      {isLoading && <CircularProgress size={24} />}
      {isError && <Alert severity="error">Could not load grouping config.</Alert>}
      {!isLoading && !isError && (
        <>
          {!groupingConfig && <Alert severity="info">No grouping config registered for this customer.</Alert>}
          <GroupingConfigForm customerId={customerId} tmc={tmc} groupingConfig={groupingConfig ?? undefined} labelledBy={labelledBy} />
        </>
      )}
    </>
  )
}

type GroupingConfigFormProps = {
  customerId: string
  tmc: TravelManagementCompany
  groupingConfig?: GroupingConfig
  labelledBy: string
}

type SpecialCaseRow = {
  key: number
  specialCase: SpecialCase
}

const GroupingConfigForm = ({ customerId, tmc, groupingConfig, labelledBy }: GroupingConfigFormProps) => {
  const notify = useMaterialNotification()
  const registerGroupingConfig = useRegisterGroupingConfig(customerId, tmc)
  const specialCasesHeadingId = useId()
  const nextKey = useRef(0)
  const toRow = (specialCase: SpecialCase): SpecialCaseRow => ({ key: nextKey.current++, specialCase })

  const [groupBy, setGroupBy] = useState<CustomerDefinedField[]>(groupingConfig?.groupBy ?? [])
  const [rows, setRows] = useState<SpecialCaseRow[]>(() => (groupingConfig?.specialCases ?? []).map(toRow))

  const updateRow = (key: number, specialCase: SpecialCase) =>
    setRows((current) => current.map((row) => (row.key === key ? { key, specialCase } : row)))

  const config: GroupingConfig = { groupBy, specialCases: rows.map(({ specialCase }) => specialCase) }
  const hasChanges = !isEqual(config, groupingConfig ?? { groupBy: [], specialCases: [] })

  const handleSave = () => registerGroupingConfig.mutate(config, { onSuccess: () => notify('Grouping config saved') })

  const canSave =
    hasChanges && groupBy.length > 0 && rows.every(({ specialCase }) => isComplete(specialCase)) && !registerGroupingConfig.isLoading

  return (
    <Box
      component="form"
      aria-labelledby={labelledBy}
      onSubmit={(e) => {
        e.preventDefault()
        handleSave()
      }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Autocomplete
        data-testid="group-by"
        multiple
        options={customerDefinedFields}
        value={groupBy}
        onChange={(_, value) => setGroupBy(inFieldOrder(value))}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Group by"
            required
            slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps.htmlInput, required: groupBy.length === 0 } }}
          />
        )}
        sx={{ width: 488 }}
      />
      <Box role="group" aria-labelledby={specialCasesHeadingId} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography id={specialCasesHeadingId} variant="body2" fontWeight="bold">
            Special cases
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Override the grouping for transactions where a field starts with a prefix.
          </Typography>
        </Box>
        {rows.map(({ key, specialCase }) => (
          <SpecialCaseFields
            key={key}
            specialCase={specialCase}
            onChange={(updated) => updateRow(key, updated)}
            onRemove={() => setRows((current) => current.filter((row) => row.key !== key))}
          />
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={() => setRows((current) => [...current, toRow(emptySpecialCase)])}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add special case
        </Button>
      </Box>
      <Divider />
      <Button type="submit" variant="contained" disabled={!canSave} sx={{ alignSelf: 'flex-end' }}>
        Save
      </Button>
    </Box>
  )
}

type SpecialCaseFieldsProps = {
  specialCase: SpecialCase
  onChange: (specialCase: SpecialCase) => void
  onRemove: () => void
}

const SpecialCaseFields = ({ specialCase, onChange, onRemove }: SpecialCaseFieldsProps) => {
  const { condition, naming } = specialCase
  const setCondition = (changes: Partial<SpecialCase['condition']>) => onChange({ ...specialCase, condition: { ...condition, ...changes } })
  const setNaming = (updated: Naming) => onChange({ ...specialCase, naming: updated })

  return (
    <Box data-testid="special-case-row" sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <TextField
        select
        label="Field"
        value={condition.field}
        onChange={(e) => setCondition({ field: e.target.value as CustomerDefinedField })}
        sx={{ width: 120 }}
      >
        {customerDefinedFields.map((field) => (
          <MenuItem key={field} value={field}>
            {field}
          </MenuItem>
        ))}
      </TextField>
      <TextField label="Prefix" value={condition.prefix} onChange={(e) => setCondition({ prefix: e.target.value })} sx={{ width: 160 }} />
      <TextField
        select
        label="Grouping"
        value={naming.type}
        onChange={(e) => setNaming(emptyNaming(e.target.value as Naming['type']))}
        sx={{ width: 160 }}
      >
        <MenuItem value="FIXED">Fixed</MenuItem>
        <MenuItem value="BY_FIELDS">By fields</MenuItem>
      </TextField>
      {naming.type === 'FIXED' ? (
        <TextField label="Value" value={naming.name} onChange={(e) => setNaming({ ...naming, name: e.target.value })} sx={{ width: 240 }} />
      ) : (
        <Autocomplete
          multiple
          options={customerDefinedFields}
          value={naming.fields}
          onChange={(_, fields) => setNaming({ ...naming, fields: inFieldOrder(fields) })}
          renderInput={(params) => <TextField {...params} label="Group by fields" />}
          sx={{ width: 240 }}
        />
      )}
      <IconButton aria-label="Remove special case" onClick={onRemove}>
        <CloseIcon />
      </IconButton>
    </Box>
  )
}
